import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SearchIcon, PackageIcon, TrendingUpIcon, ClockIcon, CheckCircle2Icon, AlertCircleIcon, XCircleIcon, FilterIcon, CalendarIcon, DollarSignIcon, BuildingIcon, ChevronRightIcon, FileTextIcon, XIcon } from 'lucide-react';
import { seedData } from '../data/seedData';
type StatusFilter = 'all' | 'In Progress' | 'Completed' | 'On Hold' | 'Cancelled';
export function Orders() {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [selectedOrder, setSelectedOrder] = useState<any>(null);
  // Aggregate all orders from all accounts
  const allOrders = useMemo(() => {
    const orders: any[] = [];
    seedData.accounts.forEach(account => {
      if (account.orders) {
        account.orders.forEach(order => {
          orders.push({
            ...order,
            accountName: account.name,
            accountId: account.id,
            accountLogo: account.logo_url
          });
        });
      }
    });
    return orders;
  }, []);
  // Filter and search
  const filteredOrders = useMemo(() => {
    return allOrders.filter(order => {
      const matchesSearch = searchQuery === '' || order.title.toLowerCase().includes(searchQuery.toLowerCase()) || order.accountName.toLowerCase().includes(searchQuery.toLowerCase()) || order.id.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [allOrders, searchQuery, statusFilter]);
  // Calculate statistics
  const stats = useMemo(() => {
    const inProgress = allOrders.filter(o => o.status === 'In Progress').length;
    const completed = allOrders.filter(o => o.status === 'Delivered').length;
    const onHold = allOrders.filter(o => o.status === 'On Hold').length;
    const totalValue = allOrders.reduce((sum, o) => sum + o.value, 0);
    return {
      inProgress,
      completed,
      onHold,
      totalValue
    };
  }, [allOrders]);
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'In Progress':
        return 'text-blue-400 bg-blue-500/10 border-blue-500/30';
      case 'Delivered':
        return 'text-green-400 bg-green-500/10 border-green-500/30';
      case 'On Hold':
        return 'text-yellow-400 bg-yellow-500/10 border-yellow-500/30';
      case 'Planning':
        return 'text-purple-400 bg-purple-500/10 border-purple-500/30';
      default:
        return 'text-slate-400 bg-slate-500/10 border-slate-500/30';
    }
  };
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'In Progress':
        return <ClockIcon className="w-4 h-4" data-id="element-2061" />;
      case 'Delivered':
        return <CheckCircle2Icon className="w-4 h-4" data-id="element-2062" />;
      case 'On Hold':
        return <AlertCircleIcon className="w-4 h-4" data-id="element-2063" />;
      case 'Planning':
        return <PackageIcon className="w-4 h-4" data-id="element-2064" />;
      default:
        return <PackageIcon className="w-4 h-4" data-id="element-2065" />;
    }
  };
  return <div className="space-y-8" data-id="element-2066">
      {/* Enhanced Header */}
      <div className="relative overflow-hidden" data-id="element-2067">
        <div className="absolute inset-0 -z-10" data-id="element-2068">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl" data-id="element-2069" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl" data-id="element-2070" />
        </div>
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5
      }} className="relative bg-gradient-to-br from-slate-900/95 via-slate-900/90 to-slate-950/95 border border-slate-800/80 rounded-3xl p-10 shadow-2xl shadow-black/40 backdrop-blur-xl" data-id="element-2071">
          <div className="flex items-start justify-between" data-id="element-2072">
            <div data-id="element-2073">
              <h1 className="text-4xl font-bold text-white mb-3 bg-gradient-to-r from-white via-blue-50 to-slate-200 bg-clip-text text-transparent" data-id="element-2074">
                Orders & Projects
              </h1>
              <p className="text-slate-400 text-lg flex items-center gap-2" data-id="element-2075">
                <PackageIcon className="w-5 h-5 text-blue-400" data-id="element-2076" />
                {filteredOrders.length} orders across your portfolio
              </p>
            </div>
            <div className="flex items-center gap-4" data-id="element-2077">
              <div className="px-5 py-2.5 bg-blue-500/10 border border-blue-500/30 rounded-xl flex items-center gap-2.5" data-id="element-2078">
                <TrendingUpIcon className="w-4 h-4 text-blue-400" data-id="element-2079" />
                <div className="text-right" data-id="element-2080">
                  <p className="text-xs text-slate-400 font-medium" data-id="element-2081">
                    Total Value
                  </p>
                  <p className="text-sm font-bold text-blue-400" data-id="element-2082">
                    ${(stats.totalValue / 1000000).toFixed(2)}M
                  </p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Enhanced Statistics Cards */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.1
    }} className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-2083">
        <div className="group relative bg-gradient-to-br from-slate-900/90 via-slate-900/80 to-slate-950/90 backdrop-blur-xl border border-slate-800/80 rounded-2xl p-6 shadow-2xl shadow-black/30 hover:border-slate-700/80 transition-all duration-300 overflow-hidden" data-id="element-2084">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-transparent to-blue-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" data-id="element-2085" />
          <div className="relative" data-id="element-2086">
            <div className="flex items-center justify-between mb-4" data-id="element-2087">
              <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center group-hover:bg-blue-500/20 transition-colors" data-id="element-2088">
                <ClockIcon className="w-6 h-6 text-blue-400" data-id="element-2089" />
              </div>
              <span className="text-3xl font-bold text-white group-hover:scale-110 transition-transform" data-id="element-2090">
                {stats.inProgress}
              </span>
            </div>
            <p className="text-sm font-semibold text-slate-400 group-hover:text-slate-300 transition-colors" data-id="element-2091">
              In Progress
            </p>
          </div>
        </div>
        <div className="group relative bg-gradient-to-br from-slate-900/90 via-slate-900/80 to-slate-950/90 backdrop-blur-xl border border-slate-800/80 rounded-2xl p-6 shadow-2xl shadow-black/30 hover:border-slate-700/80 transition-all duration-300 overflow-hidden" data-id="element-2092">
          <div className="absolute inset-0 bg-gradient-to-br from-green-500/5 via-transparent to-green-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" data-id="element-2093" />
          <div className="relative" data-id="element-2094">
            <div className="flex items-center justify-between mb-4" data-id="element-2095">
              <div className="w-12 h-12 rounded-xl bg-green-500/10 flex items-center justify-center group-hover:bg-green-500/20 transition-colors" data-id="element-2096">
                <CheckCircle2Icon className="w-6 h-6 text-green-400" data-id="element-2097" />
              </div>
              <span className="text-3xl font-bold text-white group-hover:scale-110 transition-transform" data-id="element-2098">
                {stats.completed}
              </span>
            </div>
            <p className="text-sm font-semibold text-slate-400 group-hover:text-slate-300 transition-colors" data-id="element-2099">
              Delivered
            </p>
          </div>
        </div>
        <div className="group relative bg-gradient-to-br from-slate-900/90 via-slate-900/80 to-slate-950/90 backdrop-blur-xl border border-slate-800/80 rounded-2xl p-6 shadow-2xl shadow-black/30 hover:border-slate-700/80 transition-all duration-300 overflow-hidden" data-id="element-2100">
          <div className="absolute inset-0 bg-gradient-to-br from-yellow-500/5 via-transparent to-yellow-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" data-id="element-2101" />
          <div className="relative" data-id="element-2102">
            <div className="flex items-center justify-between mb-4" data-id="element-2103">
              <div className="w-12 h-12 rounded-xl bg-yellow-500/10 flex items-center justify-center group-hover:bg-yellow-500/20 transition-colors" data-id="element-2104">
                <AlertCircleIcon className="w-6 h-6 text-yellow-400" data-id="element-2105" />
              </div>
              <span className="text-3xl font-bold text-white group-hover:scale-110 transition-transform" data-id="element-2106">
                {stats.onHold}
              </span>
            </div>
            <p className="text-sm font-semibold text-slate-400 group-hover:text-slate-300 transition-colors" data-id="element-2107">
              On Hold
            </p>
          </div>
        </div>
      </motion.div>

      {/* Enhanced Search & Filter Bar */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.2
    }} className="relative bg-gradient-to-br from-slate-900/90 via-slate-900/80 to-slate-950/90 backdrop-blur-xl border border-slate-800/80 rounded-2xl p-6 shadow-2xl shadow-black/30" data-id="element-2108">
        <div className="flex flex-col lg:flex-row gap-4" data-id="element-2109">
          {/* Search Input */}
          <div className="flex-1 relative" data-id="element-2110">
            <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" data-id="element-2111" />
            <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search by order name, ID, or account..." className="w-full pl-12 pr-4 py-4 bg-slate-900/60 border border-slate-800/80 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500/40 transition-all duration-200 shadow-lg shadow-black/20" data-id="element-2112" />
            {searchQuery && <button onClick={() => setSearchQuery('')} className="absolute right-4 top-1/2 -translate-y-1/2 p-1 text-slate-500 hover:text-white transition-colors" data-id="element-2113">
                <XIcon className="w-4 h-4" data-id="element-2114" />
              </button>}
          </div>
          {/* Status Filter Buttons */}
          <div className="flex gap-2 pb-1" data-id="element-2115">
            <button onClick={() => setStatusFilter('all')} className={`px-6 py-3.5 rounded-xl text-sm font-semibold transition-all duration-200 ${statusFilter === 'all' ? 'bg-gradient-to-r from-purple-600 to-purple-700 text-white ring-1 ring-purple-500/30 scale-[1.01]' : 'bg-slate-800/60 text-slate-400 hover:text-white hover:bg-slate-700/60 border border-slate-700/50 hover:border-slate-600 hover:scale-[1.01]'}`} data-id="element-2116">
              All
            </button>
            <button onClick={() => setStatusFilter('In Progress')} className={`px-6 py-3.5 rounded-xl text-sm font-semibold transition-all duration-200 ${statusFilter === 'In Progress' ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white ring-1 ring-blue-500/30 scale-[1.01]' : 'bg-slate-800/60 text-slate-400 hover:text-white hover:bg-slate-700/60 border border-slate-700/50 hover:border-slate-600 hover:scale-[1.01]'}`} data-id="element-2117">
              In Progress
            </button>
            <button onClick={() => setStatusFilter('Completed')} className={`px-6 py-3.5 rounded-xl text-sm font-semibold transition-all duration-200 ${statusFilter === 'Completed' ? 'bg-gradient-to-r from-green-600 to-green-700 text-white ring-1 ring-green-500/30 scale-[1.01]' : 'bg-slate-800/60 text-slate-400 hover:text-white hover:bg-slate-700/60 border border-slate-700/50 hover:border-slate-600 hover:scale-[1.01]'}`} data-id="element-2118">
              Delivered
            </button>
            <button onClick={() => setStatusFilter('On Hold')} className={`px-6 py-3.5 rounded-xl text-sm font-semibold transition-all duration-200 ${statusFilter === 'On Hold' ? 'bg-gradient-to-r from-yellow-600 to-yellow-700 text-white ring-1 ring-yellow-500/30 scale-[1.01]' : 'bg-slate-800/60 text-slate-400 hover:text-white hover:bg-slate-700/60 border border-slate-700/50 hover:border-slate-600 hover:scale-[1.01]'}`} data-id="element-2119">
              On Hold
            </button>
          </div>
        </div>
      </motion.div>

      {/* Enhanced Orders Grid */}
      {filteredOrders.length === 0 ? <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.3
    }} className="relative bg-gradient-to-br from-slate-900/90 via-slate-900/80 to-slate-950/90 backdrop-blur-xl border border-slate-800/80 rounded-2xl p-16 shadow-2xl shadow-black/30 text-center" data-id="element-2120">
          <div className="w-20 h-20 rounded-2xl bg-slate-800/60 flex items-center justify-center mx-auto mb-6" data-id="element-2121">
            <PackageIcon className="w-10 h-10 text-slate-500" data-id="element-2122" />
          </div>
          <h3 className="text-xl font-bold text-white mb-2" data-id="element-2123">No Orders Found</h3>
          <p className="text-slate-400" data-id="element-2124">
            Try adjusting your search or filter criteria
          </p>
        </motion.div> : <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-2125">
          {filteredOrders.map((order, index) => <motion.div key={order.id} initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1 + index * 0.02
      }} onClick={() => setSelectedOrder(order)} className="group relative bg-gradient-to-br from-slate-900/90 via-slate-900/80 to-slate-950/90 backdrop-blur-xl border border-slate-800/80 rounded-2xl p-6 shadow-2xl shadow-black/30 hover:border-blue-500/40 transition-all duration-300 cursor-pointer overflow-hidden hover:scale-[1.02]" data-id="element-2126">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-transparent to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" data-id="element-2127" />
              <div className="relative" data-id="element-2128">
                {/* Order ID Badge */}
                <div className="mb-4" data-id="element-2129">
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-slate-950/80 border border-slate-700/60 rounded-lg" data-id="element-2130">
                    <PackageIcon className="w-3.5 h-3.5 text-blue-400" data-id="element-2131" />
                    <span className="text-sm font-semibold text-slate-300 tracking-wide" data-id="element-2132">
                      {order.id}
                    </span>
                  </div>
                </div>
                {/* Order Title and Status */}
                <div className="flex items-start justify-between gap-4 mb-5" data-id="element-2133">
                  <h3 className="text-xl font-bold text-white leading-tight group-hover:text-blue-400 transition-colors flex-1" data-id="element-2134">
                    {order.title}
                  </h3>
                  <div className={`px-3 py-1.5 rounded-lg text-xs font-bold border flex items-center gap-1.5 flex-shrink-0 ${getStatusColor(order.status)}`} data-id="element-2135">
                    {getStatusIcon(order.status)}
                    {order.status}
                  </div>
                </div>
                {/* Order Details Grid */}
                <div className="grid grid-cols-2 gap-3 mb-5" data-id="element-2136">
                  <div className="p-4 bg-slate-950/60 rounded-xl border border-slate-800/60 group-hover:border-slate-700/60 transition-colors" data-id="element-2137">
                    <div className="flex items-center gap-2 mb-2" data-id="element-2138">
                      <DollarSignIcon className="w-4 h-4 text-green-400" data-id="element-2139" />
                      <span className="text-xs text-slate-500 font-semibold uppercase tracking-wider" data-id="element-2140">
                        Value
                      </span>
                    </div>
                    <p className="text-lg font-bold text-white" data-id="element-2141">
                      ${(order.value / 1000).toFixed(0)}K
                    </p>
                  </div>
                  <div className="p-4 bg-slate-950/60 rounded-xl border border-slate-800/60 group-hover:border-slate-700/60 transition-colors" data-id="element-2142">
                    <div className="flex items-center gap-2 mb-2" data-id="element-2143">
                      <CalendarIcon className="w-4 h-4 text-blue-400" data-id="element-2144" />
                      <span className="text-xs text-slate-500 font-semibold uppercase tracking-wider" data-id="element-2145">
                        Date
                      </span>
                    </div>
                    <p className="text-sm font-bold text-white" data-id="element-2146">
                      {new Date(order.date).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric'
                })}
                    </p>
                  </div>
                </div>
                {/* Account Badge */}
                <div className="pt-4 border-t border-slate-800/60 group-hover:border-slate-700/60 transition-colors" data-id="element-2147">
                  <div className="flex items-center justify-between" data-id="element-2148">
                    <div className="flex items-center gap-3 flex-1 min-w-0" data-id="element-2149">
                      <div className="w-10 h-10 rounded-xl bg-white/95 p-2 flex items-center justify-center shadow-lg ring-1 ring-slate-700/30 group-hover:ring-blue-500/40 transition-all flex-shrink-0" data-id="element-2150">
                        <img src={order.accountLogo} alt={order.accountName} className="w-full h-full object-contain" data-id="element-2151" />
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-2152">
                        <p className="text-xs text-slate-500 mb-0.5 font-semibold uppercase tracking-wider" data-id="element-2153">
                          Account
                        </p>
                        <p className="text-sm font-semibold text-slate-300 truncate group-hover:text-white transition-colors" data-id="element-2154">
                          {order.accountName}
                        </p>
                      </div>
                    </div>
                    <ChevronRightIcon className="w-5 h-5 text-slate-600 group-hover:text-blue-400 group-hover:translate-x-1 transition-all duration-200 flex-shrink-0" data-id="element-2155" />
                  </div>
                </div>
              </div>
            </motion.div>)}
        </div>}

      {/* Order Detail Modal */}
      <AnimatePresence data-id="element-2156">
        {selectedOrder && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setSelectedOrder(null)} className="fixed inset-0 bg-black/75 backdrop-blur-md z-[100]" data-id="element-2157" />
            <div className="fixed inset-0 z-[101] overflow-y-auto" data-id="element-2158">
              <div className="flex min-h-full items-center justify-center p-4" data-id="element-2159">
                <motion.div initial={{
              opacity: 0,
              scale: 0.95
            }} animate={{
              opacity: 1,
              scale: 1
            }} exit={{
              opacity: 0,
              scale: 0.95
            }} transition={{
              duration: 0.3,
              ease: [0.16, 1, 0.3, 1]
            }} onClick={e => e.stopPropagation()} className="relative w-full max-w-2xl bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 border border-slate-800/80 rounded-3xl shadow-2xl overflow-hidden" data-id="element-2160">
                  {/* Modal Header */}
                  <div className="relative px-8 py-6 border-b border-slate-800/80 bg-gradient-to-r from-slate-950 via-slate-900 to-slate-950" data-id="element-2161">
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 via-transparent to-purple-500/5 pointer-events-none" data-id="element-2162" />
                    <div className="relative flex items-start justify-between gap-6" data-id="element-2163">
                      <div className="flex-1 min-w-0" data-id="element-2164">
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-slate-950/80 border border-slate-700/60 rounded-lg mb-3" data-id="element-2165">
                          <PackageIcon className="w-3.5 h-3.5 text-blue-400" data-id="element-2166" />
                          <span className="text-sm font-semibold text-slate-300 tracking-wide" data-id="element-2167">
                            {selectedOrder.id}
                          </span>
                        </div>
                        <h2 className="text-2xl font-bold text-white mb-3 bg-gradient-to-r from-white to-slate-200 bg-clip-text text-transparent" data-id="element-2168">
                          {selectedOrder.title}
                        </h2>
                        <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold border shadow-lg ${getStatusColor(selectedOrder.status)}`} data-id="element-2169">
                          {getStatusIcon(selectedOrder.status)}
                          {selectedOrder.status}
                        </div>
                      </div>
                      <button onClick={() => setSelectedOrder(null)} className="p-2.5 hover:bg-slate-800/60 rounded-xl transition-all text-slate-400 hover:text-white hover:scale-105 flex-shrink-0" data-id="element-2170">
                        <XIcon className="w-6 h-6" data-id="element-2171" />
                      </button>
                    </div>
                  </div>
                  {/* Modal Content */}
                  <div className="p-8 space-y-6 max-h-[calc(85vh-140px)] overflow-y-auto scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-transparent" data-id="element-2172">
                    {/* Order Details */}
                    <div className="space-y-4" data-id="element-2173">
                      <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider flex items-center gap-2" data-id="element-2174">
                        <FileTextIcon className="w-4 h-4" data-id="element-2175" />
                        Order Details
                      </h3>
                      <div className="grid grid-cols-2 gap-4" data-id="element-2176">
                        <div className="p-6 bg-slate-900/60 border border-slate-800/60 rounded-xl hover:border-slate-700/60 transition-all" data-id="element-2177">
                          <div className="flex items-center gap-2 mb-3" data-id="element-2178">
                            <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center" data-id="element-2179">
                              <DollarSignIcon className="w-5 h-5 text-green-400" data-id="element-2180" />
                            </div>
                            <span className="text-xs text-slate-500 font-semibold uppercase tracking-wider" data-id="element-2181">
                              Order Value
                            </span>
                          </div>
                          <p className="text-2xl font-bold text-white" data-id="element-2182">
                            ${selectedOrder.value.toLocaleString('en-US')}
                          </p>
                        </div>
                        <div className="p-6 bg-slate-900/60 border border-slate-800/60 rounded-xl hover:border-slate-700/60 transition-all" data-id="element-2183">
                          <div className="flex items-center gap-2 mb-3" data-id="element-2184">
                            <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center" data-id="element-2185">
                              <CalendarIcon className="w-5 h-5 text-blue-400" data-id="element-2186" />
                            </div>
                            <span className="text-xs text-slate-500 font-semibold uppercase tracking-wider" data-id="element-2187">
                              Order Date
                            </span>
                          </div>
                          <p className="text-lg font-bold text-white" data-id="element-2188">
                            {new Date(selectedOrder.date).toLocaleDateString('en-US', {
                          month: 'long',
                          day: 'numeric',
                          year: 'numeric'
                        })}
                          </p>
                        </div>
                      </div>
                    </div>
                    {/* Account Information */}
                    <div className="space-y-4" data-id="element-2189">
                      <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider flex items-center gap-2" data-id="element-2190">
                        <BuildingIcon className="w-4 h-4" data-id="element-2191" />
                        Account
                      </h3>
                      <div className="flex items-center gap-5 p-6 bg-slate-900/60 border border-slate-800/60 rounded-xl hover:border-slate-700/60 transition-all" data-id="element-2192">
                        <div className="w-16 h-16 rounded-xl bg-white/95 p-3 flex items-center justify-center shadow-lg ring-2 ring-slate-700/30 flex-shrink-0" data-id="element-2193">
                          <img src={selectedOrder.accountLogo} alt={selectedOrder.accountName} className="w-full h-full object-contain" data-id="element-2194" />
                        </div>
                        <div data-id="element-2195">
                          <p className="text-xs text-slate-500 mb-1 font-semibold uppercase tracking-wider" data-id="element-2196">
                            Client Account
                          </p>
                          <p className="text-lg font-bold text-white" data-id="element-2197">
                            {selectedOrder.accountName}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </>}
      </AnimatePresence>
    </div>;
}